import { useState, useEffect } from 'react';
import { useCalendarPage } from '../contexts/CalendarPageContext';
import { useCurrentCalendar } from '../contexts/CurrentCalendarContext';

export default function EventsStatusIndicator() {
  const { theme } = useCurrentCalendar();
  const {
    calColor,
    eventsIsLoading,
    eventsIsFetching,
    eventsIsError,
    eventsError,
    refetchEvents,
  } = useCalendarPage();

  const [isOnline, setIsOnline] = useState(
    () => navigator.onLine
  );

  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  // Error state takes priority
  if (eventsIsError) {
    return (
      <div className="h-1 mb-2">
        <div className="rounded-sm overflow-hidden bg-red-50 border border-red-200 h-full">
          <div className="flex items-center justify-between px-3 py-2 h-full">
            <span className="text-sm text-red-700">
              Failed to load events
              {eventsError && (
                <span className="text-red-600 ml-1">
                  — {eventsError.message || 'Unknown error'}
                </span>
              )}
            </span>
            <button
              onClick={() => refetchEvents()}
              className="text-sm font-medium text-red-700 hover:text-red-900 underline transition-colors"
            >
              Retry
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Offline state
  if (!isOnline) {
    return (
      <div className="h-1 mb-2">
        <div className="flex items-center gap-2 h-full">
          <span
            className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-mono"
            style={{
              backgroundColor: theme.surface3,
              color: '#3d3530',
            }}
          >
            <svg
              className="w-3.5 h-3.5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636"
              />
            </svg>
            Offline — using cached events
          </span>
        </div>
      </div>
    );
  }

  // Loading state - show when initially loading or refetching
  // Container always has fixed height to prevent jitter
  return (
    <div className="h-1 mb-2">
      {(eventsIsLoading || eventsIsFetching) && (
        <div className="h-full w-full overflow-hidden rounded-full bg-gray-100">
          <div
            className="h-full animate-pulse"
            style={{
              backgroundColor: calColor,
              animation:
                'loading-bar 1.5s ease-in-out infinite',
            }}
          />
          <style>{`
            @keyframes loading-bar {
              0% { width: 0%; margin-left: 0%; }
              50% { width: 50%; margin-left: 25%; }
              100% { width: 0%; margin-left: 100%; }
            }
          `}</style>
        </div>
      )}
    </div>
  );
}
